import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Modal,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  Button,
  Snackbar,
  Alert,
} from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import { useModalContext } from "./ModalContext";
import api from "./axiosConfig";
import LoadingSpinner from "./components/LoadingSpinner";
import { formatUpdatedCurrentMonthData } from "./helpers/currentMonthData";
import { fetchEditPermissions } from "./api/editpermissions";

const ModalComponent = ({ activeFilters, updateRowData }) => {
  const table_list = [
    { name: "Actual", displayname: "Sale" },
    { name: "Rate", displayname: "Rate" },
    { name: "Baseline", displayname: "Baseline" },
    { name: "Correction", displayname: "Baseline" },
    { name: "On Inv", displayname: "On Inv" },
    { name: "Off Inv", displayname: "Off Inv" },
    { name: "Visibility", displayname: "Visibility" },
  ];
  const {
    openModal,
    editingValue,
    lineData,
    handleCloseModal,
    closeModalwithValues,
    lineDataClick,
    setSaveLoading,
    saveLoading,
    setFilters,
  } = useModalContext();
  setFilters(activeFilters);
  const [percentage, setPercentage] = useState("");
  const [rate, setRate] = useState("");
  const [error, setError] = useState("");
  const [isButtonEnabled, setIsButtonEnabled] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  //edit permission
  useEffect(() => {
    const fetchPermission = async () => {
      const permissions = await fetchEditPermissions();

      // Check permissions based on month type
      const isCurrentMonth = permissions.Current_Month_Cell === 1;
      const isFutureMonth = permissions.Future_Month_Cell === 1;

      setIsButtonEnabled(isCurrentMonth);
    };

    fetchPermission();
  }, []);

  const handlePercentageChange = (e) => {
    const value = e.target.value;

    if (!/^(-|\-?\d+(\.\d*)?)?$/.test(value)) {
      setError("Please enter a valid number");
    } else {
      setError("");
      setPercentage(value);
    }
  };

  const handleRateChange = (e) => {
    const value = e.target.value;

    if (!/^(-|\-?\d+(\.\d*)?)?$/.test(value)) {
      setError("Please enter a valid number");
    } else {
      setError("");
      setRate(value);
    }
  };

  const handleSave = () => {
    if (error || !percentage) {
      setError("Please enter a valid percentage");
      return;
    }

    const dataToSend = {
      ...lineDataClick,
      percentage: Number(percentage),
      rate: Number(rate),
    };

    setSaveLoading(true);
    api
      .post("/updatewitheditprocessnew", { body: dataToSend })
      .then((data) => {
        setSaveLoading(false);
        handleCloseModal_view();
        setPercentage("");
        setRate("");
        const fortmatedData = formatUpdatedCurrentMonthData(
          data.data.data_updated[0]
        );
        updateRowData(fortmatedData);
        setSnackbarMessage("Your data has been updated.");
        setSnackbarOpen(true); // Show the snackbar
      })
      .catch((error) => {
        console.error("Error updating data:", error);
        setSaveLoading(false);
      });
  }; 

  const handleCancelClick = (index) => {
    setSaveLoading(true);
    api
      .post("/deleteprocess", { body: lineDataClick })
      .then(() => {
        setSaveLoading(false);
      })
      .catch((error) => {
        console.error("Error canceling process:", error);
        setSaveLoading(false);
      });
  };

  const handleCloseModal_view = () => {
    closeModalwithValues();
  };

  const calculateNewTotal = () => {
    const baseline = editingValue.baseline || 0;
    const correction = percentage
      ? Number(percentage)
      : editingValue.correction || 0;
    const scheme = editingValue.scheme || 0;
    return baseline + correction + scheme;
  };

  const hideTextField = lineData.some(
    (item) => item.Request_Type === "Correction"
  );

  const combinedData = table_list.map((staticRow) => {
    // Find the matching item in lineData
    const matchingItem = lineData.find(
      (item) => item.Request_Type === staticRow.name
    );

    // Return the merged item or default Sales to 0 if missing
    return {
      Request_Type: staticRow.name,
      Sales: matchingItem ? matchingItem.Sales : 0,
    };
  });
  const totalSales = combinedData.reduce(
    (sum, item) => sum + parseInt(item.Sales || 0),
    0
  );

  const handleSnackbarClose = () => {
    setSnackbarOpen(false); // Close the snackbar
  };

  return (
    <>
      <Modal
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby='modal-modal-title'
        aria-describedby='modal-modal-description'
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 800,
            maxHeight: "80vh",
            overflowY: "auto",
            bgcolor: "background.paper",
            // border: "2px solid #000",
            boxShadow: 24,
          }}
        >
          <Typography
            sx={{
              color: "#fff",
              backgroundColor: "#16a695",
              padding: "20px",
              position: "sticky",
              top: 0,
              zIndex: 10,
              padding: "25px",
            }}
            id='modal-modal-title'
            variant='h6'
            component='h2'
          >
            Edit Value
          </Typography>
          <TableContainer
            sx={{
              padding: "25px",
            }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Request Type</TableCell>
                  <TableCell>Sales</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {combinedData.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>{item.Request_Type}</TableCell>
                    <TableCell>{Math.ceil(item.Sales)}</TableCell>
                    <TableCell>
                      {item.Request_Type === "Correction" && item.Sales != 0 ? (
                        <IconButton onClick={() => handleCancelClick(index)}>
                          <CancelIcon />
                        </IconButton>
                      ) : (
                        item.Request_Type === "Correction" &&
                        item.Sales == 0 && (
                          <TextField
                            label='Correction % '
                            value={percentage}
                            onChange={handlePercentageChange}
                            error={!!error}
                            helperText={error}
                            fullWidth
                            sx={{ mt: 2 }}
                          />
                        )
                      )}

                      {item.Request_Type === "Rate" && item.Sales != 0 ? (
                        <IconButton onClick={() => handleCancelClick(index)}>
                          <CancelIcon />
                        </IconButton>
                      ) : (
                        item.Request_Type === "Rate" &&
                        item.Sales == 0 && (
                          <TextField
                            label='Rate'
                            value={rate}
                            onChange={handleRateChange}
                            error={!!error}
                            helperText={error}
                            fullWidth
                            sx={{ mt: 2 }}
                          />
                        )
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell>Total</TableCell>
                  <TableCell>{totalSales}</TableCell>
                  <TableCell> </TableCell>
                </TableRow>

                {/* {lineData && (
<TableContainer component={Paper}>
<Table>
<TableHead>
<TableRow>
<TableCell>Request Type</TableCell>
<TableCell>Sales</TableCell>
<TableCell>Action</TableCell>
</TableRow>
</TableHead>
<TableBody>

  {
      {table_list.map((item, index) => (
        <TableRow key={index}>
        <TableCell>{item.displayname}</TableCell>
        <TableCell></TableCell>
        <TableCell>
                      {item.name === "Correction" && (
            <IconButton onClick={() => handleCancelClick(index)}>
            <CancelIcon />
            </IconButton>
                      )}
      </TableCell>
              </TableRow>

      ))}

  } */}
                {/* {lineData.map((item, index) => (
<TableRow key={index}>
<TableCell>{item.Request_Type}</TableCell>
<TableCell>{Math.ceil(item.Sales)}</TableCell>
<TableCell>
                      {item.Request_Type === "Correction" && (
<IconButton onClick={() => handleCancelClick(index)}>
<CancelIcon />
</IconButton>
                      )}
</TableCell>
</TableRow>
                ))} */}
              </TableBody>
            </Table>
          </TableContainer>
          {/* )} */}

          {/* <Box sx={{ mt: 2 }}>
<Typography>Baseline: {editingValue.baseline}</Typography>
<Typography>Correction: {editingValue.correction}</Typography>
<Typography>Scheme: {editingValue.scheme} (non-editable)</Typography>
<Typography>Total: {calculateNewTotal().toLocaleString()}</Typography>
</Box> */}

          {/* {!hideTextField && (
<TextField
            label="Provide % for Correction"
            value={percentage}
            onChange={handlePercentageChange}
            error={!!error}
            helperText={error}
            fullWidth
            sx={{ mt: 2 }}
          />
        )} */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "right",
              padding: "25px",
              marginLeft: "auto",
            }}
          >
            <Button
              onClick={handleCloseModal_view}
              variant='contained'
              style={{}}
              sx={{
                mr: 2,

                color: "#16a695",
                borderColor: "#16a695",
                background: "#fff",
              }}
            >
              Close
            </Button>
            {!hideTextField && (
              <Button
                style={{}}
                onClick={handleSave}
                variant='contained'
                sx={{
                  backgroundColor: !isButtonEnabled ? "#e0e0e0" : "#16a695",
                  color: !isButtonEnabled ? "#9e9e9e" : "#fff",
                  cursor: !isButtonEnabled ? "not-allowed" : "pointer",
                }}
              >
                Save
              </Button>
            )}
          </Box>

          {saveLoading && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                backgroundColor: "rgba(255, 255, 255, 0.8)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 9999,
              }}
            >
              <LoadingSpinner />
            </Box>
          )}
        </Box>
      </Modal>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity='success'
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ModalComponent;
